import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { AttachmentRef } from '@heybesafe/shared';
import { ensureUrlScheme } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import LoadingPane from '../../components/LoadingPane';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { HbsButton } from '../../components/hbs/Button';
import { Icon } from '../../components/hbs/Icon';
import LinkedItemsSection from '../../components/LinkedItemsSection';
import DocumentBodyView from '../../components/DocumentBodyView';
import { resolveLinkedItems } from '../../binder/resolveLinkedItems';
import { base64UrlToBytes } from '../../crypto/base64url';
import { downloadAttachment } from '../../lib/attachments';

/**
 * Read-only family document detail. The rich experience (photo thumbnails, a
 * linked schedule card, in-place editing) is a follow-on — this renders the
 * document's title, body, and links so a created document isn't a dead-end.
 */
export default function DocumentDetailPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, orgs = [], getFamilyPrivateKey } = useSafe();
  const categoriesByOrg = useFamilyCategories();

  const item = items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'document');
  const org = orgs.find((o) => o.id === orgId);
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === (item?.categoryId ?? categoryId));
  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading document…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Document not found.</p>
        <HbsButton variant="plain" id="document-not-found-back-button" onClick={() => navigate(backTo)}>Back</HbsButton>
      </div>
    );
  }

  const links = (item.links ?? []).filter((l) => l.url.trim());
  // Include reverse-linked schedules (schedules whose relatedItemId points at
  // this document) so the box-visibility check matches what LinkedItemsSection
  // renders.
  const resolved = resolveLinkedItems(item.relatedItemIds, items, orgId ?? '', item.id);
  const hasLinkedItems = resolved.contacts.length + resolved.documents.length + resolved.schedules.length > 0;
  const hasRelatedContent = links.length > 0 || hasLinkedItems;

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      <div>
        <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
          {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
        </p>
        <h1 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text">
          {item.name}
        </h1>
        <Link
          to={`/orgs/${orgId}/binder/${categoryId}/documents/${item.id}/edit`}
          className="text-[13px] font-semibold text-hbs-primary hover:underline"
        >
          Edit
        </Link>
      </div>

      {/* Document body — its own light box. Rendered as Markdown (bold/italic/
          lists); see DocumentBodyView / client-contract §3.7.1.x. */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <DocumentBodyView markdown={item.body ?? ''} />
      </section>

      {(item.attachments?.length ?? 0) > 0 && org?.publicKey && getFamilyPrivateKey(orgId!) && (
        <DocumentPhotos
          attachments={item.attachments!}
          orgId={orgId!}
          itemId={item.id}
          familyPublicKeyB64={org.publicKey}
          familyPrivateKey={getFamilyPrivateKey(orgId!)!}
        />
      )}

      {/* Related content (links + linked Binder items) — a separate light box. */}
      {hasRelatedContent && (
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 flex flex-col gap-6">
          {links.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">Related Web Sites</div>
              <ul className="flex flex-col gap-1">
                {links.map((l, i) => (
                  <li key={`${l.url}-${i}`}>
                    <a
                      href={ensureUrlScheme(l.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[14px] text-hbs-primary hover:underline"
                    >
                      <Icon name="launch" size={14} />
                      {l.label || l.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <LinkedItemsSection orgId={orgId!} categoryId={categoryId} relatedItemIds={item.relatedItemIds} itemId={item.id} />
        </section>
      )}

      <div>
        <HbsButton variant="plain" id="document-back-to-category-button" onClick={() => navigate(backTo)}>Back to {category?.name ?? 'category'}</HbsButton>
      </div>
    </div>
  );
}

function DocumentPhotos({
  attachments, orgId, itemId, familyPublicKeyB64, familyPrivateKey,
}: {
  attachments: AttachmentRef[];
  orgId: string;
  itemId: string;
  familyPublicKeyB64: string;
  familyPrivateKey: Uint8Array;
}): JSX.Element {
  const [thumbs, setThumbs] = useState<Record<string, string | 'error'>>({});
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);
  const familyPublicKey = useMemo(() => base64UrlToBytes(familyPublicKeyB64), [familyPublicKeyB64]);

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    (async () => {
      for (const ref of attachments) {
        try {
          const url = await downloadAttachment(
            ref,
            { orgId, itemId, familyPublicKey, familyPrivateKey },
            { thumb: true },
          );
          created.push(url);
          if (!cancelled) setThumbs((p) => ({ ...p, [ref.attachmentId]: url }));
        } catch {
          if (!cancelled) setThumbs((p) => ({ ...p, [ref.attachmentId]: 'error' }));
        }
      }
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [attachments, orgId, itemId, familyPublicKey, familyPrivateKey]);

  // Revoke the full-res lightbox object URL if the component unmounts while
  // the lightbox is open — otherwise unmounting mid-view (e.g. navigating
  // away) leaks one decrypted-image blob URL. Explicit close/re-open already
  // revoke via closeLightbox/openFull; this covers the unmount case.
  useEffect(() => () => { if (lightbox) URL.revokeObjectURL(lightbox.url); }, [lightbox]);

  // Escape dismisses the open lightbox, mirroring the click-anywhere-to-close
  // affordance. Inlines closeLightbox's body so the effect depends only on
  // `lightbox` (closeLightbox is re-created each render).
  useEffect(() => {
    if (!lightbox) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        URL.revokeObjectURL(lightbox!.url);
        setLightbox(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  async function openFull(ref: AttachmentRef): Promise<void> {
    try {
      const url = await downloadAttachment(ref, { orgId, itemId, familyPublicKey, familyPrivateKey });
      setLightbox((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return { url, alt: ref.filename };
      });
    } catch {
      /* leave the grid intact */
    }
  }

  function closeLightbox(): void {
    if (lightbox) URL.revokeObjectURL(lightbox.url);
    setLightbox(null);
  }

  return (
    <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
      <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-3">Photos</div>
      <ul className="flex flex-wrap gap-3">
        {attachments.map((ref) => {
          const state = thumbs[ref.attachmentId];
          return (
            <li key={ref.attachmentId}>
              {state === 'error' ? (
                <div className="w-24 h-24 flex items-center justify-center bg-hbs-bg border border-hbs-border rounded-hbs-sm text-[11px] text-hbs-text-mute">
                  Unavailable
                </div>
              ) : state ? (
                <button type="button" id={`document-attachment-${ref.filename}`} onClick={() => openFull(ref)} className="block">
                  <img src={state} alt={ref.filename} className="w-24 h-24 object-cover rounded-hbs-sm border border-hbs-border" />
                </button>
              ) : (
                <div className="w-24 h-24 bg-hbs-bg border border-hbs-border rounded-hbs-sm animate-pulse" />
              )}
            </li>
          );
        })}
      </ul>

      {lightbox && (
        <div
          role="dialog"
          aria-label={lightbox.alt}
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-8"
        >
          <img src={lightbox.url} alt={lightbox.alt} className="max-h-full max-w-full rounded-hbs-sm" />
        </div>
      )}
    </section>
  );
}
